import React, { Fragment } from 'react';
import { Path } from '@react-pdf/renderer';
import { values } from 'lodash';

/**
 * Interface for wall configuration
 */
interface Walls {
  top?: boolean;
  right?: boolean;
  bottom?: boolean;
  left?: boolean;
}

/**
 * Interface for area dimensions
 */
interface Dimensions {
  length: number;
  width: number;
}

/**
 * Interface for area location
 */
interface Location {
  top?: number;
  left?: number;
}

/**
 * Interface for area with walls
 */
interface RectWithWalls extends Dimensions, Location {
  walls?: Walls;
}

/**
 * Interface for area data
 */
interface Area {
  dimensions: Dimensions;
  location: Location;
  surroundingWalls: Walls;
  [key: string]: any;
}

/**
 * Interface for areas object
 */
interface Areas {
  [key: string]: Area;
}

/**
 * Interface for content
 */
interface Content {
  dimensions: Dimensions;
  areas: Areas;
  [key: string]: any;
}

/**
 * Interface for line data
 */
interface LineData {
  d: string;
  dash: boolean | undefined;
}

/**
 * Interface for component props
 */
interface MuiPartitionsProps {
  inContainer?: boolean;
  areaId?: string;
  scale: number;
  content: Content;
}

/**
 * MUI Partitions component that replaces the styled-components Partitions component
 * 
 * This component provides a Material UI equivalent of the original Partitions component
 * with similar props and functionality. It's used for PDF generation.
 * 
 * @param {MuiPartitionsProps} props - Component properties
 * @returns {JSX.Element} MUI Partitions component
 */
const MuiPartitions: React.FC<MuiPartitionsProps> = (props) => {
  const { inContainer = false, areaId, scale, content: { dimensions, areas } } = props;
  
  // Create rectangles from dimensions and areas
  const rectangles: RectWithWalls[] = [
    // Include container dimensions if in container mode
    ...(inContainer ? [dimensions] : []), 
    // Include specific area if areaId provided, otherwise include all areas
    ...(areaId ? (areas?.[areaId] ? [areas[areaId]] : []) : values(areas))
      .map(a => ({ 
        ...a.dimensions, 
        ...a.location, 
        walls: a.surroundingWalls 
      }))
  ];
  
  // Generate lines from rectangles
  const lines: LineData[] = rectangles.flatMap(rect => {
    const { top: tw = true, right: rw = true, bottom: bw = true, left: lw = true } = rect.walls || {};
    return [...Array(2)].flatMap((_, index) => {
      const odd = index % 2;
      const sign = odd ? -1 : 1;
      const x0 = 1 + ((inContainer ? 1 : 0) * (rect.left || 0) + odd * rect.length) * scale;
      const y0 = 1 + ((inContainer ? 1 : 0) * (rect.top || 0) + odd * rect.width) * scale;
      const x1 = x0 + sign * rect.length * scale;
      const y1 = y0 + sign * rect.width * scale;
      return [
        { d: `m${x0} ${y0}H${x1}`, dash: odd ? bw : tw }, 
        { d: `m${x0} ${y0}V${y1}`, dash: odd ? rw : lw }
      ];
    });
  });
  
  return (
    <Fragment>
      {lines.map((line, index) => (
        <Path 
          key={`line-${index}`} 
          d={line.d} 
          stroke="#000" 
          strokeWidth={1.5} 
          strokeDasharray={line.dash ? 'none' : '12'} 
        />
      ))}
    </Fragment>
  );
};

export default MuiPartitions;
